import { ref } from 'vue'
import { db } from '@/firebase/firebase'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch
} from 'firebase/firestore'

export function useCommunityReportAdmin() {
  const loading = ref(false)
  const error = ref(null)

  // 이메일 마스킹
  const maskEmail = (email) => {
    if (!email) return '익명'
    const [local, domain] = email.split('@')
    if (!domain) return '익명'
    const visiblePart = local.slice(0, 3)
    return `${visiblePart}****@${domain}`
  }

  // 시간 포맷
  const formatTimeAgo = (dateString) => {
    if (!dateString) return ''
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now - date
    const diffMin = Math.floor(diffMs / 60000)
    const diffHour = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHour / 24)

    if (diffMin < 1) return '방금 전'
    if (diffMin < 60) return `${diffMin}분 전`
    if (diffHour < 24) return `${diffHour}시간 전`
    if (diffDay < 7) return `${diffDay}일 전`

    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${month}/${day}`
  }

  // 신고 목록 조회
  const getReports = async (options = {}) => {
    loading.value = true
    error.value = null

    const {
      type = 'all', // all, post, comment
      status = 'all', // all, pending, resolved, dismissed
      pageSize = 50
    } = options

    try {
      let q = query(
        collection(db, 'communityReports'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      )

      const snapshot = await getDocs(q)
      let reports = []

      for (const reportDoc of snapshot.docs) {
        const data = reportDoc.data()
        let contentInfo = null

        // 신고 대상 정보 가져오기
        if (data.type === 'post' && data.postId) {
          const postDoc = await getDoc(doc(db, 'communityPosts', data.postId))
          if (postDoc.exists()) {
            const postData = postDoc.data()
            contentInfo = {
              title: postData.title,
              content: postData.content,
              authorEmail: postData.userEmail,
              isBlinded: postData.isBlinded || false,
              reportCount: postData.reportCount || 0
            }
          }
        } else if (data.type === 'comment' && data.commentId) {
          const commentDoc = await getDoc(doc(db, 'communityComments', data.commentId))
          if (commentDoc.exists()) {
            const commentData = commentDoc.data()
            contentInfo = {
              content: commentData.content,
              authorEmail: commentData.userEmail,
              isBlinded: commentData.isBlinded || false,
              reportCount: commentData.reportCount || 0
            }
          }
        }

        reports.push({
          id: reportDoc.id,
          ...data,
          contentInfo,
          maskedReporterEmail: maskEmail(data.reporterEmail),
          maskedAuthorEmail: contentInfo ? maskEmail(contentInfo.authorEmail) : '알 수 없음',
          timeAgo: formatTimeAgo(data.createdAt)
        })
      }

      // 필터링
      if (type !== 'all') {
        reports = reports.filter(r => r.type === type)
      }
      if (status !== 'all') {
        reports = reports.filter(r => r.status === status)
      }

      return reports
    } catch (err) {
      console.error('신고 목록 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  // 블라인드 처리
  const blindContent = async (reportId, type, targetId) => {
    loading.value = true
    error.value = null

    try {
      const collectionName = type === 'post' ? 'communityPosts' : 'communityComments'
      const targetRef = doc(db, collectionName, targetId)

      await updateDoc(targetRef, {
        isBlinded: true,
        blindedAt: new Date().toISOString()
      })

      // 신고 상태 업데이트
      await updateDoc(doc(db, 'communityReports', reportId), {
        status: 'resolved',
        resolvedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('블라인드 처리 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 블라인드 해제
  const unblindContent = async (reportId, type, targetId) => {
    loading.value = true
    error.value = null

    try {
      const collectionName = type === 'post' ? 'communityPosts' : 'communityComments'
      const targetRef = doc(db, collectionName, targetId)

      await updateDoc(targetRef, {
        isBlinded: false,
        unblindedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('블라인드 해제 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 신고 처리 (무시)
  const dismissReport = async (reportId) => {
    loading.value = true
    error.value = null

    try {
      await updateDoc(doc(db, 'communityReports', reportId), {
        status: 'dismissed',
        dismissedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('신고 처리 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 게시글/댓글 삭제
  const deleteContent = async (reportId, type, targetId, postId = null) => {
    loading.value = true
    error.value = null

    try {
      const batch = writeBatch(db)

      if (type === 'post') {
        // 게시글 삭제
        batch.delete(doc(db, 'communityPosts', targetId))

        // 댓글도 함께 삭제
        const commentsQuery = query(
          collection(db, 'communityComments'),
          where('postId', '==', targetId)
        )
        const commentsSnapshot = await getDocs(commentsQuery)
        commentsSnapshot.docs.forEach(commentDoc => {
          batch.delete(doc(db, 'communityComments', commentDoc.id))
        })

        // 좋아요도 함께 삭제
        const likesQuery = query(
          collection(db, 'communityLikes'),
          where('postId', '==', targetId)
        )
        const likesSnapshot = await getDocs(likesQuery)
        likesSnapshot.docs.forEach(likeDoc => {
          batch.delete(doc(db, 'communityLikes', likeDoc.id))
        })

        // 북마크도 함께 삭제
        const bookmarksQuery = query(
          collection(db, 'communityBookmarks'),
          where('postId', '==', targetId)
        )
        const bookmarksSnapshot = await getDocs(bookmarksQuery)
        bookmarksSnapshot.docs.forEach(bookmarkDoc => {
          batch.delete(doc(db, 'communityBookmarks', bookmarkDoc.id))
        })

        // 관련 신고 모두 resolved 처리
        const reportsQuery = query(
          collection(db, 'communityReports'),
          where('postId', '==', targetId)
        )
        const reportsSnapshot = await getDocs(reportsQuery)
        reportsSnapshot.docs.forEach(reportDoc => {
          batch.update(doc(db, 'communityReports', reportDoc.id), {
            status: 'resolved',
            resolvedAt: new Date().toISOString()
          })
        })
      } else if (type === 'comment') {
        // 댓글 삭제
        batch.delete(doc(db, 'communityComments', targetId))

        // 게시글의 댓글 수 감소
        if (postId) {
          batch.update(doc(db, 'communityPosts', postId), {
            commentCount: -1 // increment(-1)은 batch에서 직접 사용 못함
          })
        }

        // 관련 신고 resolved 처리
        batch.update(doc(db, 'communityReports', reportId), {
          status: 'resolved',
          resolvedAt: new Date().toISOString()
        })
      }

      await batch.commit()

      return { success: true }
    } catch (err) {
      console.error('콘텐츠 삭제 실패:', err)
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  // 통계 조회
  const getReportStats = async () => {
    try {
      const q = query(collection(db, 'communityReports'))
      const snapshot = await getDocs(q)

      const stats = {
        total: snapshot.size,
        pending: 0,
        resolved: 0,
        dismissed: 0,
        postReports: 0,
        commentReports: 0
      }

      snapshot.docs.forEach(doc => {
        const data = doc.data()
        if (data.status === 'pending') stats.pending++
        else if (data.status === 'resolved') stats.resolved++
        else if (data.status === 'dismissed') stats.dismissed++

        if (data.type === 'post') stats.postReports++
        else if (data.type === 'comment') stats.commentReports++
      })

      return stats
    } catch (err) {
      console.error('통계 조회 실패:', err)
      return {
        total: 0,
        pending: 0,
        resolved: 0,
        dismissed: 0,
        postReports: 0,
        commentReports: 0
      }
    }
  }

  return {
    loading,
    error,
    getReports,
    blindContent,
    unblindContent,
    dismissReport,
    deleteContent,
    getReportStats
  }
}
